import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import {
  FieldValue,
  FieldValues,
  FieldValuesSchema,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormLayoutItem } from 'features/libra/sections/formLayout'
import { Section } from 'features/libra/sections/sectionTypes'
import {
  applyOriginOption,
  FieldValuesSetter,
} from 'features/libra/ticket/fieldOrigin'
import { toPersistedShape } from 'features/libra/ticket/fieldValuesPatch'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useActiveScopedFieldAccess } from 'features/libra/ticket/useScopedFieldAccess'
import { useWorkflow } from 'features/libra/WorkflowContext'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'
import { useCallback, useMemo, useRef, useState } from 'react'

import { deriveStepFieldValues } from './deriveStepFieldValues'

/** `wrapper` undefined means the user removed the key. */
type UnsavedFieldEdit = { wrapper: FieldValue | undefined; revision: number }

const withUnsavedEdits = (
  storedFieldValues: FieldValues,
  unsavedEdits: Record<string, UnsavedFieldEdit>
): FieldValues => {
  const merged: FieldValues = { ...storedFieldValues }
  for (const [key, { wrapper }] of Object.entries(unsavedEdits)) {
    if (wrapper === undefined) delete merged[key]
    else merged[key] = wrapper
  }
  return merged
}

const persistedWrapper = (key: string, wrapper: FieldValue | undefined) =>
  toPersistedShape(wrapper === undefined ? {} : { [key]: wrapper })[key]

const matchesStoredValue = (
  storedFieldValues: FieldValues,
  key: string,
  wrapper: FieldValue | undefined
) =>
  isEqual(
    persistedWrapper(key, storedFieldValues[key]),
    persistedWrapper(key, wrapper)
  )

type UseWorkflowFieldValuesOptions = {
  ontologyFields: Record<string, OntologyField>
  sections: ReadonlyArray<Section>
  form: ReadonlyArray<FormLayoutItem>
}

export const useWorkflowFieldValues = ({
  ontologyFields,
  sections,
  form,
}: UseWorkflowFieldValuesOptions) => {
  const activeStepId = useActiveStepId()
  const workflow = useWorkflow()
  const access = useActiveScopedFieldAccess()
  const { isLoading } = access
  const ticketFieldValues = access.fieldValues
  const saveFieldValues = access.setFieldValues
  const branch = useMemo(
    () => workflow.steps.find((step) => step.id === activeStepId)?.branch,
    [activeStepId, workflow.steps]
  )

  const initialFieldValues = useMemo<FieldValues>(
    () => FieldValuesSchema.parse(ticketFieldValues ?? {}),
    [ticketFieldValues]
  )

  // Stored field values come from the ticket (Rolodex); `unsavedEdits` holds only
  // what the user changed locally and is cleared once `saveFieldValues` writes it.
  // An edit back to the stored value is dropped unless a save is in flight, so
  // later ticket refreshes still show through.
  const [unsavedEdits, setUnsavedEdits] = useState<
    Record<string, UnsavedFieldEdit>
  >({})
  const editRevisionRef = useRef(0)
  const inFlightSaveCountRef = useRef(0)
  const storedFieldValuesRef = useRef(initialFieldValues)
  storedFieldValuesRef.current = initialFieldValues

  const fieldValuesWithUnsavedEdits = useMemo(
    () => withUnsavedEdits(initialFieldValues, unsavedEdits),
    [initialFieldValues, unsavedEdits]
  )

  const [drawingResetKey, setDrawingResetKey] = useState(0)

  const setFieldValues = useCallback<FieldValuesSetter>((action, option) => {
    setUnsavedEdits((prev) => {
      const merged = withUnsavedEdits(storedFieldValuesRef.current, prev)
      const next = applyOriginOption(
        typeof action === 'function' ? action(merged) : action,
        option
      )
      const nextEdits: Record<string, UnsavedFieldEdit> = { ...prev }
      let changed = false
      for (const key of new Set([
        ...Object.keys(merged),
        ...Object.keys(next),
      ])) {
        if (merged[key] === next[key] || isEqual(merged[key], next[key]))
          continue
        changed = true
        if (
          inFlightSaveCountRef.current === 0 &&
          matchesStoredValue(storedFieldValuesRef.current, key, next[key])
        ) {
          delete nextEdits[key]
        } else {
          nextEdits[key] = {
            wrapper: next[key],
            revision: ++editRevisionRef.current,
          }
        }
      }
      return changed ? nextEdits : prev
    })
  }, [])

  const { allVisibleSections, resolvedFieldValues, fieldValuesToSave } =
    useMemo(
      () =>
        deriveStepFieldValues({
          fieldValues: fieldValuesWithUnsavedEdits ?? {},
          sections,
          form,
          ontologyFields,
          branch,
        }),
      [fieldValuesWithUnsavedEdits, sections, form, ontologyFields, branch]
    )

  /** What Save would write (`fieldValuesToSave`, incl. defaults, computed and branch writes) differs from saved. */
  const hasUnsavedEditsOrSystemValues = useMemo(
    () =>
      createPatchData(
        'id',
        toPersistedShape(initialFieldValues),
        toPersistedShape(fieldValuesToSave)
      ) !== undefined,
    [fieldValuesToSave, initialFieldValues]
  )

  /** Local values (typed or AI-filled) differ from saved. */
  const hasUnsavedEdits = useMemo(
    () =>
      createPatchData(
        'id',
        toPersistedShape(initialFieldValues),
        toPersistedShape(fieldValuesWithUnsavedEdits)
      ) !== undefined,
    [fieldValuesWithUnsavedEdits, initialFieldValues]
  )

  const handleCancel = useCallback(() => {
    setUnsavedEdits({})
    setDrawingResetKey((current) => current + 1)
  }, [])

  const handleSave = useCallback(async () => {
    const submittedEdits = unsavedEdits
    inFlightSaveCountRef.current += 1
    let written: boolean | undefined
    try {
      written = await saveFieldValues(fieldValuesToSave)
    } finally {
      inFlightSaveCountRef.current -= 1
    }
    if (written !== true) return
    setUnsavedEdits((prev) => {
      const unsubmitted = omitBy(
        prev,
        (edit, key) =>
          submittedEdits[key] && submittedEdits[key].revision === edit.revision
      )
      if (inFlightSaveCountRef.current > 0) return unsubmitted
      return omitBy(unsubmitted, (edit, key) =>
        matchesStoredValue(storedFieldValuesRef.current, key, edit.wrapper)
      )
    })
  }, [unsavedEdits, fieldValuesToSave, saveFieldValues])

  return {
    fieldValues: fieldValuesWithUnsavedEdits,
    resolvedFieldValues,
    initialFieldValues,
    drawingResetKey,
    setFieldValues,
    allVisibleSections,
    hasUnsavedEditsOrSystemValues,
    hasUnsavedEdits,
    handleCancel,
    handleSave,
    isLoading,
  }
}

export type UseWorkflowFieldValuesReturn = ReturnType<
  typeof useWorkflowFieldValues
>
